import { createEmptyCard, fsrs, generatorParameters, Rating, State, type Card, type Grade as FsrsGrade } from 'ts-fsrs';
import { dayKey, endOfDay, parseDay } from './day';

export type CardKind = 'say' | 'hear';
/** Say-it cards are self-graded; hear-it cards are checked by the pick. */
export type Grade = 'missed' | 'got' | 'instant' | 'wrong' | 'right';

/** An FSRS card as stored: dates as ISO strings so it survives JSON. */
export type StoredCard = Omit<Card, 'due' | 'last_review'> & { due: string; last_review?: string };

export interface CardRecord {
  id: string;
  itemId: string;
  kind: CardKind;
  fsrs: StoredCard;
  /** Learning day the card was introduced. */
  introduced: string;
  /** How many times the card has been shown; alternates the model voice. */
  presentations: number;
  /** The phrase's text when the card was made; a changed text restarts the card. */
  text: string;
}

export const DESIRED_RETENTION = 0.9;
export const MASTERED_DAYS = 30;

const scheduler = fsrs(
  generatorParameters({
    request_retention: DESIRED_RETENTION,
    enable_fuzz: true,
    // Within-session repeats are handled by the session itself (a missed card comes back once),
    // so FSRS schedules whole days only.
    enable_short_term: false,
    maximum_interval: 3650,
  }),
);

const RATINGS: Record<Grade, FsrsGrade> = {
  missed: Rating.Again,
  wrong: Rating.Again,
  got: Rating.Good,
  right: Rating.Good,
  instant: Rating.Easy,
};

export function cardId(itemId: string, kind: CardKind): string {
  return `${itemId}:${kind}`;
}

function toStored(card: Card): StoredCard {
  const { due, last_review, ...rest } = card;
  return { ...rest, due: due.toISOString(), ...(last_review ? { last_review: last_review.toISOString() } : {}) };
}

function fromStored(card: StoredCard): Card {
  const { due, last_review, ...rest } = card;
  return { ...rest, due: new Date(due), ...(last_review ? { last_review: new Date(last_review) } : {}) } as Card;
}

/** A new card. `availableFrom` holds it back, e.g. a phrase's second card until the next day. */
export function newCard(itemId: string, kind: CardKind, text: string, day: string, now: Date, availableFrom?: Date): CardRecord {
  const card = createEmptyCard(now);
  if (availableFrom) card.due = availableFrom;
  return { id: cardId(itemId, kind), itemId, kind, fsrs: toStored(card), introduced: day, presentations: 0, text };
}

/** Applies a grade with FSRS and returns the updated card. */
export function review(record: CardRecord, grade: Grade, now: Date): CardRecord {
  const card = fromStored(record.fsrs);
  // FSRS counts days between reviews in UTC calendar days. Reviewing "at noon of the learning
  // day" makes those days match the learner's own days (which start at 04:00) in any time zone.
  const at = parseDay(dayKey(now));
  // A held-back card answered early (e.g. via "learn early") is treated as due now.
  if (card.state === State.New && card.due > at) card.due = at;
  const next = scheduler.next(card, at, RATINGS[grade]);
  return { ...record, fsrs: toStored(next.card) };
}

/**
 * The quick check right after meeting a phrase is a learning step, not a review: a correct answer
 * minutes after hearing the model says little about memory. The card stays new and comes back
 * tomorrow, where its first real answer sets the schedule (so reviews fall on about day 1, 3,
 * 10 and 30 rather than starting at 3 or 8 days).
 */
export function learningStep(record: CardRecord, now: Date): CardRecord {
  return { ...record, fsrs: { ...record.fsrs, due: endOfDay(now).toISOString() } };
}

export function dueDate(record: CardRecord): Date {
  return new Date(record.fsrs.due);
}

/** Due on the learning day of `now` (before the next 04:00). */
export function isDue(record: CardRecord, now: Date): boolean {
  return dueDate(record) < endOfDay(now);
}

export type CardStatus = 'new' | 'learning' | 'known' | 'mastered';

export function cardStatus(record: CardRecord): CardStatus {
  const { state, scheduled_days, reps } = record.fsrs;
  if (reps === 0 || state === State.New) return 'new';
  if (state === State.Review && scheduled_days >= MASTERED_DAYS) return 'mastered';
  if (state === State.Review && scheduled_days >= 7) return 'known';
  return 'learning';
}

/** Recall probability right now, from FSRS's forgetting curve. */
export function retrievability(record: CardRecord, now: Date): number | null {
  const card = fromStored(record.fsrs);
  if (card.state === State.New) return null;
  return scheduler.get_retrievability(card, now, false) as number;
}

export { State };
